import React, { useCallback, useLayoutEffect, useRef } from 'react';

import {
  useAutofillDetector,
  useFocusDetector,
  useForwardedRefLocally,
  useIdCounter,
  useNativeInvalidDetector,
} from '../../hooks';
import { useResizeObserver } from '../../hooks/use-resize-observer';
import { useThrottled } from '../../hooks/use-throttled';
import { combineAttributeStrings, getRequiredLabel } from '../../utils/internal';
import { InputMessage } from '../input-message';
import {
  TextFieldInner,
  TextFieldLabel,
  TextFieldWrapper,
} from '../text-fieldish/text-fieldish.styled';
import { TextFieldState } from '../text-fieldish/types';

import { StyledTextArea, TextAreaSizeReference } from './text-area.styled';
import { ITextAreaProps } from './types';

/**
 * Keeps the textarea height automatically adjusted to match its contents.
 *
 * Auto-resizing will be skipped if the user manually adjusts the height of the
 * textarea once.
 *
 * Auto-resizing is handy for the UX of the TextArea for desktop users, but
 * indispensable for mobile users, who (at least on iOS) have no way to resize a
 * textarea element manually.
 */
function useAutoResize(
  originalRef: React.MutableRefObject<HTMLTextAreaElement | null>,
  sizeReferenceRef: React.MutableRefObject<HTMLTextAreaElement | null>,
  value: string,
) {
  // This just tracks the auto-sized height we have assigned so we can
  // differentiate between auto resizes and user-triggered resizes
  const autoHeightRef = useRef<string | undefined>();

  const autoSize = useCallback(() => {
    if (!originalRef.current || !sizeReferenceRef.current) {
      return;
    }

    const { style } = originalRef.current;
    const { style: refStyle } = sizeReferenceRef.current;
    if (autoHeightRef.current !== undefined && style.height !== autoHeightRef.current) {
      // assume the height was manually adjusted by the user and bail
      return;
    }

    // Padding calculation START
    // We take this opportunity to calculate the ideal padding.
    // The reason a padding calculation is needed in the first place is that we
    // want a single-line TextArea to look nearly identical in dimension and
    // spacing to the single-line TextInput. TextInputs vertically center their
    // text by default, so they did not have to calculate a padding which,
    // paired with the line height of the current font, added up to the standard
    // height of the TextInput. TextAreas, however, do not have that vertical
    // centering behavior, and no CSS rules can set it, either. So without
    // padding, all the text of a TextArea appears bunched-up in the top. We
    // want to set a padding to give the illusion of vertical centering, but the
    // changing line height means giving the element a fixed padding can either
    // result in (1) the text being off-vertical-center (with a small padding),
    // or (2) the height of the single-line box being stretched out beyond the
    // standard one-line height (with a large padding). So rather than setting
    // fixed padding values inside a media query that would be brittle to token
    // or media query changes, we calculate the ideal padding here, with the
    // understanding that the padding can become incorrect (not fatally so,
    // though) if the screen size changes, at least until the `value` of the
    // TextArea is updated again. All of this calculation can be avoided with
    // `calc` in the CSS once we are able to use `lh` units:
    // https://caniuse.com/?search=lh%20unit
    refStyle.paddingTop = '0px';
    refStyle.paddingBottom = '0px';

    // This uses our min-height set in CSS to get the ideal pixel height of
    // a single-line textarea
    const minHeightTarget = sizeReferenceRef.current.offsetHeight;

    refStyle.minHeight = '0px';

    // With min-height and padding stripped away and height set to auto,
    // offsetHeight gives us the line height of the text in pixels.
    // getComputedStyle would potentially be useful for calculating the same
    // thing, but there's no guarantee it will return a pixel value.
    const lineHeight = sizeReferenceRef.current.offsetHeight;
    const paddingHeight = `${(minHeightTarget - lineHeight) / 2}px`;

    refStyle.minHeight = ''; // resetting value used for calculation
    refStyle.paddingTop = paddingHeight;
    refStyle.paddingBottom = paddingHeight;
    // Padding calculation END

    // Use the scrollHeight to calculate the height of the text content plus
    // padding, and set that as the element height
    const nextHeight = `${sizeReferenceRef.current.scrollHeight}px`;
    autoHeightRef.current = nextHeight;

    style.height = nextHeight;
    style.paddingTop = paddingHeight;
    style.paddingBottom = paddingHeight;
  }, [originalRef, sizeReferenceRef, autoHeightRef]);

  // Readjust the size whenever the dimensions of the textarea change (e.g.,
  // when its container's width changes, the window is resized, etc.)
  const throttledCb = useThrottled(autoSize, 300);
  useResizeObserver(originalRef, throttledCb);

  // Readjust the size whenever the value of the textarea changes (so we can add
  // or remove height based on how many lines the text occupies)
  useLayoutEffect(autoSize, [value, autoSize]);
}

/**
 * Allows for the input of potentially long passages of text with newlines
 */
const TextArea = React.forwardRef<HTMLTextAreaElement, ITextAreaProps>(
  (
    {
      // Custom props
      'data-testid': testId,
      disableScreenReaderAlert,
      errorMessage,
      forceErrorState,
      hintMessage,
      label,
      labelId,

      // Native input element props
      'aria-describedby': ariaDescribedBy,
      'aria-label': ariaLabel,
      autoComplete,
      autoFocus,
      disabled,
      id,
      inputMode,
      maxLength,
      name,
      onAnimationStart,
      onBlur,
      onChange,
      onFocus,
      onInvalid,
      required,
      value,
    },
    forwardedRef,
  ) => {
    const defaultInputId = useIdCounter('floating-label-input');
    const messageId = useIdCounter('message');
    const inputId = id ?? defaultInputId;

    const wrapperTestId = testId ? `${testId}-wrapper` : undefined;
    const labelTestId = testId ? `${testId}-label` : undefined;
    const messageTestId = testId ? `${testId}-message` : undefined;

    const sizeReferenceRef = useRef<HTMLTextAreaElement>(null);
    const { localRef, refForProps } = useForwardedRefLocally<HTMLTextAreaElement>(forwardedRef);
    const { isNativeInvalid, handleInvalid } = useNativeInvalidDetector(localRef, onInvalid);
    const { isAutofilled, handleAnimationStart } = useAutofillDetector(onAnimationStart);
    const { handleBlur, handleFocus, isFocused } = useFocusDetector({
      onBlur,
      onFocus,
    });

    useAutoResize(localRef, sizeReferenceRef, value);

    const stateAttrs: { [k in keyof TextFieldState]-?: TextFieldState[k] } = {
      $disabled: !!disabled,
      $focused: isFocused,
      $autofilled: isAutofilled,
      $hasError: forceErrorState || !!errorMessage || isNativeInvalid,
      $hasValue: !!value,
      $isDate: false,
      $iconCount: 0,
    };

    return (
      <TextFieldWrapper data-testid={wrapperTestId}>
        <TextFieldInner {...stateAttrs}>
          <TextFieldLabel {...stateAttrs} id={labelId} htmlFor={inputId} data-testid={labelTestId}>
            {getRequiredLabel(label, required)}
          </TextFieldLabel>

          <StyledTextArea
            {...stateAttrs}
            aria-describedby={combineAttributeStrings(
              hintMessage || errorMessage ? messageId : undefined,
              ariaDescribedBy,
            )}
            aria-invalid={stateAttrs.$hasError || undefined}
            aria-label={ariaLabel}
            as="textarea"
            autoComplete={autoComplete}
            autoFocus={autoFocus}
            data-private
            data-testid={testId}
            disabled={disabled}
            id={inputId}
            inputMode={inputMode}
            maxLength={maxLength}
            name={name}
            onAnimationStart={handleAnimationStart}
            onBlur={handleBlur}
            onChange={onChange}
            onFocus={handleFocus}
            onInvalid={handleInvalid}
            ref={refForProps}
            required={required}
            value={value}
          />

          {/*
            An extra textarea is used as a size reference for calculating the
            ideal height for our primary textarea. Originally the component
            used just the primary textarea to calculate its own ideal height,
            but the intermediate modifications to the textarea made to
            calculate the height, while invisible to users, created
            undesireable behavior when paired with the behavior of mobile
            device software keyboards (specifically, that of iOS Safari):
            the view would scroll up to the top of the textarea any time a
            newline was entered or the text wrapped, despite the actively
            edited text being far below that.

            On an initial attempt, a div containing identical text to the
            textarea was used as the reference element, but matching the exact
            display behavior of a textarea is more tricky than it may seem, and
            requires workarounds that may need to be adjusted on a per-browser
            basis to work consistently across browsers. So a duplicate textarea
            is more compatible and future-proof, though it does introduce some
            other challenges addressed with the props below.
          */}
          <TextAreaSizeReference
            {...stateAttrs}
            as="textarea"
            ref={sizeReferenceRef}
            value={value}
            // set rows to 1 to make height calculation work right
            rows={1}
            // The following props attempt to make the textarea invisible to
            // users and the forms it may be contained in
            aria-hidden
            name={undefined}
            disabled // this and the absence of a name prevents it from being submitted with forms
            tabIndex={-1}
          />
        </TextFieldInner>

        {(errorMessage || hintMessage) && (
          <InputMessage
            id={messageId}
            disableScreenReaderAlert={disableScreenReaderAlert}
            data-testid={messageTestId}
            hasError={Boolean(errorMessage)}
            disabled={stateAttrs.$disabled}
          >
            {errorMessage || hintMessage}
          </InputMessage>
        )}
      </TextFieldWrapper>
    );
  },
);

export default TextArea;
